import { DatePipe, formatDate } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ChartType } from 'angular-google-charts';
import { Subscription } from 'rxjs';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { MessageService } from '../../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { KieneTableService } from '../../../components/kiene-table/kiene-table.service';
import { CsvColumn } from '../../../csv-export/csv-export/csv-column';
import { Supplier } from '../../../warehouse/supplier/supplier';
import { ReportUtils } from '../../report-utils';
import { ZeitauswahlChange } from '../../zeitauswahl/zeitauswahl-change';
import { LieferantEinzel } from './lieferant-einzel';

@Component({
    selector: 'kiene-lieferant-einzel-report',
    templateUrl: './lieferant-einzel-report.component.html',
    styleUrls: ['./lieferant-einzel-report.component.scss'],
})
export class LieferantEinzelReportComponent implements OnInit, AfterViewInit {
    displayedColumns = [
        'artikel',
        'artikelmenge',
        'umsatz_prozent',
        'umsatz',
        'gutschriften',
        'gutschriften_prozent',
        'rabatte',
        'rabatte_prozent',
        'endpreis',
        'bestellungen',
    ];
    dataSource = new MatTableDataSource<LieferantEinzel>();
    dataSourceVorjahr = new MatTableDataSource<LieferantEinzel>();
    @ViewChild(MatSort) sort: MatSort;
    expandedElement: LieferantEinzel | null;

    currentSupplier: Supplier;

    reportParams = new HttpParams();

    zeitauswahl: ZeitauswahlChange;
    vorjahreszeitraum: ZeitauswahlChange;

    chartType = ChartType.PieChart;
    chartColumns = ['Artikel', 'Umsatz'];
    chartData = [['', 0]];

    csvColumns: CsvColumn[] = [];
    csvColumnsDetails: CsvColumn[] = [];
    csvFilename = 'Lieferanten_Einzel.csv';

    gesamtUmsatz = 0;
    gesamtUmsatzII = 0;
    gesamtUmsatzIIProzent = '0';
    gesamtGutschriften = 0;
    gesamtGutschriftenProzent = '0';
    gesamtRabatte = 0;
    gesamtRabatteProzent = '0';
    gesamtBestellungen = 0;

    gesamtUmsatzVorjahr = 0;
    gesamtUmsatzIIVorjahr = 0;
    gesamtUmsatzIIProzentVorjahr = '0';
    gesamtGutschriftenVorjahr = 0;
    gesamtGutschriftenProzentVorjahr = '0';
    gesamtRabatteVorjahr = 0;
    gesamtRabatteProzentVorjahr = '0';
    gesamtBestellungenVorjahr = 0;

    isLoading: boolean[] = [];
    aktuellSubscription: Subscription;
    vorjahrSubscription: Subscription;

    constructor(
        private readonly api: KieneTableService,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly datePipe: DatePipe,
        private readonly messageService: MessageService,
    ) {}

    ngOnInit(): void {
        this.reportParams = this.reportParams.set('von', this.datePipe.transform(new Date(), 'yyyy-MM-dd'));
        this.reportParams = this.reportParams.set('bis', this.datePipe.transform(new Date(), 'yyyy-MM-dd'));

        this.csvColumns.push(new CsvColumn('artikel', 'Artikel'));
        this.csvColumns.push(new CsvColumn('artikelmenge', 'Menge'));
        this.csvColumns.push(new CsvColumn('umsatz_prozent', 'Umsatz von Gesamt (%)', '%'));
        this.csvColumns.push(new CsvColumn('umsatz', 'Umsatz'));
        this.csvColumns.push(new CsvColumn('gutschriften', 'Gutschriften'));
        this.csvColumns.push(new CsvColumn('gutschriften_prozent', 'Gutschriften (%)', '%'));
        this.csvColumns.push(new CsvColumn('rabatte', 'Rabatte'));
        this.csvColumns.push(new CsvColumn('rabatte_prozent', 'Rabatte (%)', '%'));
        this.csvColumns.push(new CsvColumn('endpreis', 'Umsatz II.'));
        this.csvColumns.push(new CsvColumn('bestellungen', 'Bestellungen'));

        this.csvColumnsDetails = this.csvColumns.map(column => ({ ...column }));
        this.csvColumnsDetails.splice(0, 1, new CsvColumn('zeitraum', 'Zeitraum'));
    }

    ngAfterViewInit() {
        this.dataSource.sort = this.sort;
    }

    loadData() {
        this.loadDataAktuell();
        this.loadDataVorjahr();
    }

    loadDataAktuell() {
        if (!this.currentSupplier) {
            return;
        }
        this.createSupplierParams();

        this.reportParams = this.reportParams.set('von', formatDate(this.zeitauswahl.von, 'yyyy-MM-dd', 'de'));
        this.reportParams = this.reportParams.set('bis', formatDate(this.zeitauswahl.bis, 'yyyy-MM-dd', 'de'));

        this.aktuellSubscription?.unsubscribe();
        this.aktuellSubscription = this.api
            .get(this.apiBaseUrl + 'report/read_lieferant_lieferant.php', this.reportParams)
            .subscribe(
                response => {
                    this.dataSource.data = response.records;
                    this.resetChartData();
                    this.berechneGesamtwerte();
                },
                error => {
                    this.messageService.errorMessage('Fehler!', error);
                },
            );
    }

    loadDataVorjahr() {
        if (!this.currentSupplier) {
            return;
        }
        this.createSupplierParams();

        this.reportParams = this.reportParams.set('von', formatDate(this.vorjahreszeitraum.von, 'yyyy-MM-dd', 'de'));
        this.reportParams = this.reportParams.set('bis', formatDate(this.vorjahreszeitraum.bis, 'yyyy-MM-dd', 'de'));

        this.vorjahrSubscription?.unsubscribe();
        this.vorjahrSubscription = this.api
            .get(this.apiBaseUrl + 'report/read_lieferant_lieferant.php', this.reportParams)
            .subscribe(
                response => {
                    this.dataSourceVorjahr.data = response.records;
                    this.berechneGesamtwerteVorjahr();
                },
                error => {
                    this.messageService.errorMessage('Fehler!', error);
                },
            );
    }

    createSupplierParams() {
        if (this.currentSupplier) {
            this.reportParams = this.reportParams.set('lieferant_id', this.currentSupplier.lieferant_id.toString());
        }
    }

    setCurrentSupplier(supplier: Supplier) {
        if (supplier) {
            this.currentSupplier = supplier;
            this.loadData();
        } else {
            this.clearAll();
        }
    }

    clearAll() {
        this.dataSource.data = [];
        this.clearGesamtwerte();
        this.resetChartData();
    }

    clearGesamtwerte() {
        this.gesamtUmsatz = 0;
        this.gesamtGutschriften = 0;
        this.gesamtRabatte = 0;
        this.gesamtBestellungen = 0;
    }

    zeitintervalChanged(event: ZeitauswahlChange) {
        this.zeitauswahl = event;
        this.vorjahreszeitraum = ReportUtils.getVorjahreszeitraum(this.zeitauswahl);
        this.loadData();
    }

    berechneGesamtwerte() {
        this.gesamtUmsatz = 0;
        this.gesamtUmsatzII = 0;
        this.gesamtGutschriften = 0;
        this.gesamtRabatte = 0;
        this.gesamtBestellungen = 0;
        const data = this.dataSource.data;
        for (const r of data) {
            this.chartData.push([r.artikel, r.umsatz ? r.umsatz : 0]);
            this.gesamtUmsatz += r.umsatz ? r.umsatz : 0;
            this.gesamtUmsatzII += r.endpreis ? r.endpreis : 0;
            this.gesamtGutschriften += r.gutschriften ? r.gutschriften : 0;
            this.gesamtRabatte += r.rabatte ? r.rabatte : 0;
            this.gesamtBestellungen += r.bestellungen ? r.bestellungen : 0;
        }
        if (this.gesamtUmsatz > 0) {
            this.gesamtGutschriftenProzent = (
                Math.round((100 / this.gesamtUmsatz) * this.gesamtGutschriften * 100) / 100
            ).toString();
            this.gesamtRabatteProzent = (
                Math.round((100 / this.gesamtUmsatz) * this.gesamtRabatte * 100) / 100
            ).toString();
            this.gesamtUmsatzIIProzent = (
                Math.round((100 / this.gesamtUmsatz) * this.gesamtUmsatzII * 100) / 100
            ).toString();
        }
    }

    berechneGesamtwerteVorjahr() {
        this.gesamtUmsatzVorjahr = 0;
        this.gesamtUmsatzIIVorjahr = 0;
        this.gesamtGutschriftenVorjahr = 0;
        this.gesamtRabatteVorjahr = 0;
        this.gesamtBestellungenVorjahr = 0;
        const data = this.dataSourceVorjahr.data;
        for (const r of data) {
            this.gesamtUmsatzVorjahr += r.umsatz ? r.umsatz : 0;
            this.gesamtUmsatzIIVorjahr += r.endpreis ? r.endpreis : 0;
            this.gesamtGutschriftenVorjahr += r.gutschriften ? r.gutschriften : 0;
            this.gesamtRabatteVorjahr += r.rabatte ? r.rabatte : 0;
            this.gesamtBestellungenVorjahr += r.bestellungen ? r.bestellungen : 0;
        }
        if (this.gesamtUmsatzVorjahr > 0) {
            this.gesamtGutschriftenProzentVorjahr = (
                Math.round((100 / this.gesamtUmsatzVorjahr) * this.gesamtGutschriftenVorjahr * 100) / 100
            ).toString();
            this.gesamtRabatteProzentVorjahr = (
                Math.round((100 / this.gesamtUmsatzVorjahr) * this.gesamtRabatteVorjahr * 100) / 100
            ).toString();
            this.gesamtUmsatzIIProzentVorjahr = (
                Math.round((100 / this.gesamtUmsatzVorjahr) * this.gesamtUmsatzIIVorjahr * 100) / 100
            ).toString();
        }
    }

    resetChartData() {
        this.chartData = [['', 0]];
    }
}
